// Removing a session from the history takes its XP back with one balancing event. Shared by the app
// ("Remove from history") and the MCP coach (delete_session), so both take back exactly the same.
import { isoWeekday, type Day } from './dates';
import { stableUuid } from './ids';
import type { HabitSession } from './model';
import { questFor, questPeriod, questStatus } from './quests';
import type { PlanLookup } from './streak';

/** "Train on a planned day" quest: any day counts without a plan, else the days with a planned session. */
export function questDayOf(planOn: PlanLookup): (day: Day) => boolean {
  return (day: Day) => {
    const plan = planOn(day);
    return !plan || plan.size === 0 || plan.has(isoWeekday(day));
  };
}

export interface XpEventLike {
  day: Day;
  amount: number;
  reason: string;
  sessionId: string | null;
}

/** Days of other sessions a quest check needs around a quest day: its 3-day period plus the 30 days quests look back. */
export const QUEST_LOOKBACK_DAYS = 31;

/**
 * The balancing XP event for a removed session: all XP it earned, except a quest's XP when the other sessions of
 * that period still finish the quest. Its id is fixed per person and session, so a second removal (in the app or
 * through the coach) never takes it back twice. null when there is nothing to take back or it was taken back already.
 * The streak is not recounted (docs/habit.md).
 */
export function sessionRemovalXp(input: {
  userId: string;
  sessionId: string;
  /** XP events of this session (others are ignored). */
  events: readonly XpEventLike[];
  /** The person's sessions without the removed one(s), at least those around the quest days. */
  others: readonly HabitSession[];
  isPlanned: (day: Day) => boolean;
  timeZone?: string;
}): { id: string; day: Day; amount: number; reason: 'session_removed'; sessionId: string } | null {
  const mine = input.events.filter((e) => e.sessionId === input.sessionId);
  if (mine.some((e) => e.reason === 'session_removed')) return null;
  const back = mine.filter((e) => {
    if (e.reason !== 'quest') return true;
    const status = questStatus({ today: e.day, sessions: [...input.others], plan: [], isPlanned: input.isPlanned, timeZone: input.timeZone });
    return !(status.done && status.quest.id === questFor(questPeriod(e.day)).id);
  });
  const total = back.reduce((sum, e) => sum + e.amount, 0);
  if (total <= 0) return null;
  return {
    id: stableUuid(`xp|${input.userId}|${input.sessionId}|removed`), day: back[0]!.day, amount: -Math.min(1000, total),
    reason: 'session_removed', sessionId: input.sessionId,
  };
}
